/**
 * 路径 2-A — §38 storage bridge
 * Save-Classroom Bridge
 *
 * High-level entry: convert prod (Stage, AppScene[], outline?) shape into a
 * MaicDocument, persist via DocumentStore.
 *
 * What this gets us:
 *   - dslVersion stamp at write time — future schema upgrades just bump
 *     RUNTIME_DSL_VERSION and the store migrates old rows on read
 *   - Per-scene incremental writes via putScene (not just full-document save)
 *   - Validation gate (assertStorableScene + injected SceneValidator)
 *
 * AppScene typing: prod callers hold AppScene (the 4-way union: slide / quiz /
 * interactive / pbl — see lib/types/stage.ts). The DSL Scene is the narrower
 * slide / quiz subset. We accept AppScene at the bridge surface and cast to DSL
 * Scene at the storage boundary; DocumentStore's SceneLike contract only checks
 * id / stageId / order, so the wider union rides along verbatim through
 * structured clone. See §37 for the SceneValidator we inject that keeps
 * validation fail-loud for the app's widened shapes.
 */
import type { MaicDocument } from '@openmaic/storage';
import type { Scene } from '@openmaic/dsl';
import type { AppScene } from '@/lib/types/stage';
import type { DongniaiStage } from './document-store-instance';
import { getDongniaiDocumentStore } from './document-store-instance';

export interface SaveClassroomBridgeInput {
  stage: DongniaiStage;
  scenes: ReadonlyArray<AppScene>;
  /** Opaque app-owned outline snapshot (NOT validated, NOT migrated). */
  outline?: unknown;
}

/**
 * Persist a (stage, scenes, outline?) triple into the BrowserDocumentStore.
 *
 * Returns the persisted MaicDocument for inspection / chaining. Throws on:
 *   - Validation failure (invalid stage or scene)
 *   - Server-side execution (no window.indexedDB)
 *   - Future-versioned stored copy (a newer client wrote this stage)
 *   - Duplicate scene ids in the input array
 */
export async function saveClassroomViaMaic(
  input: SaveClassroomBridgeInput,
): Promise<MaicDocument<Scene>> {
  const store = getDongniaiDocumentStore();
  const doc: MaicDocument<Scene> = {
    stage: input.stage,
    scenes: input.scenes as unknown as Scene[],
    outline: input.outline,
  };
  await store.saveDocument(doc);
  return doc;
}

export interface PutSceneBridgeInput {
  stageId: string;
  scene: AppScene;
}

/**
 * Incremental scene write — use for single-scene edits without rewriting
 * the whole document. Requires the stored document to be at the current
 * DSL version; if the document is stale or newer, throws — caller must
 * loadDocument + migrate + saveDocument first.
 */
export async function putSceneViaMaic(input: PutSceneBridgeInput): Promise<void> {
  const store = getDongniaiDocumentStore();
  await store.putScene(input.stageId, input.scene as unknown as Scene);
}